import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  useTheme,
  Tooltip,
} from '@mui/material';
import {
  SwapHoriz,
  Info,
  TrendingUp,
  TrendingDown,
  QueryStats,
  Loop,
  CalendarToday,
  Schedule
} from '@mui/icons-material';
import { DialogHeader } from '../common/AppDialog';

const AccountBalanceDetailDialog = ({
  open,
  onClose,
  accountId,
  accountName,
  month,
  isForecast,
  balanceDetails,
  transactions, // New prop for actual transactions
  formatCurrency
}) => {
  const theme = useTheme();
  const [monthlyTransactions, setMonthlyTransactions] = useState([]);
  const [moneyIn, setMoneyIn] = useState(0);
  const [moneyOut, setMoneyOut] = useState(0);
  const [adjustment, setAdjustment] = useState(0);

  // Process transactions for current/past months
  useEffect(() => {
    if (!accountId || !month || !transactions) return;
    
    // Skip processing for forecast months - we'll use balanceDetails instead
    if (isForecast) return;
    
    // Parse month string to get date range
    const [monthAbbr, yearStr] = month.split('-');
    const monthIndex = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(monthAbbr);
    const year = 2000 + parseInt(yearStr, 10);
    
    const startDate = new Date(year, monthIndex, 1);
    const endDate = new Date(year, monthIndex + 1, 0); // Last day of month
    
    // Filter transactions for this account and month
    const filteredTransactions = transactions
      .filter(t => 
        t.account === accountId && 
        new Date(t.date) >= startDate &&
        new Date(t.date) <= endDate
      )
      .sort((a, b) => new Date(a.date) - new Date(b.date));
    
    setMonthlyTransactions(filteredTransactions);
    
    // Calculate money in and out
    let totalIn = 0;
    let totalOut = 0;
    
    filteredTransactions.forEach(transaction => {
      const amount = transaction.amount || 0;
      
      if (amount > 0) {
        totalIn += amount;
      } else {
        totalOut += Math.abs(amount);
      }
    });
    
    const net = totalIn - totalOut;
    
    // Calculate adjustment if needed
    let adjustmentAmount = 0;
    
    if (balanceDetails) {
      const actualChange = balanceDetails.endingBalance - balanceDetails.startingBalance;
      adjustmentAmount = actualChange - net;
      
      // Only set adjustment if it's significant (handles floating point precision)
      if (Math.abs(adjustmentAmount) < 0.01) {
        adjustmentAmount = 0;
      }
    }
    
    setMoneyIn(totalIn);
    setMoneyOut(totalOut);
    setAdjustment(adjustmentAmount);
    
  }, [accountId, month, transactions, isForecast, balanceDetails]);

  // If no data is provided, show a message
  if (!balanceDetails || !balanceDetails.startingBalance) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogHeader title={`${accountName || 'Account'} · ${month || 'Month'}`} onClose={onClose} />
        <DialogContent>
          <Typography color="text.secondary">No balance details available for this account and month.</Typography>
        </DialogContent>
      </Dialog>
    );
  }

  const {
    startingBalance,
    budgetMovements = [],
    actionMovements = [],
    totalBudgetMovement = 0,
    totalActionMovement = 0,
    endingBalance
  } = balanceDetails;

  // Calculate total movements - for forecast, use budget + action, for actual use moneyIn - moneyOut + adjustment
  // Important: We're just using this for display in the Movements section, not for recalculating the ending balance
  const totalMovement = isForecast 
    ? totalBudgetMovement + totalActionMovement 
    : moneyIn - moneyOut + adjustment;

  // Get frequency icon
  const getFrequencyIcon = (frequency) => {
    switch (frequency) {
      case 'weekly':
        return <Schedule fontSize="small" sx={{ color: theme.palette.info.main }} />;
      case 'monthly':
        return <CalendarToday fontSize="small" sx={{ color: theme.palette.info.main }} />;
      case 'yearly':
        return <Loop fontSize="small" sx={{ color: theme.palette.info.main }} />;
      case 'once':
      default:
        return null;
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogHeader
        title={`${accountName || 'Account'} as at ${month || 'Month'}`}
        onClose={onClose}
        action={isForecast ? (
          <Chip icon={<QueryStats />} label="Forecast" color="secondary" size="small" />
        ) : undefined}
      />
      <DialogContent>
        <Box mb={3}>
          {/* Starting Balance Box */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 3, boxShadow: 1 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1" fontWeight="bold">Starting Balance</Typography>
              <Typography 
                variant="h6" 
                color={startingBalance >= 0 ? 'success.main' : 'error.main'}
                fontWeight="bold"
              >
                {formatCurrency(startingBalance)}
              </Typography>
            </Box>
          </Paper>

          {/* Movement Summary - Different for actual vs forecast */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 3, boxShadow: 1 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle1" fontWeight="bold">Movements</Typography>
              <Typography 
                variant="h6" 
                color={totalMovement >= 0 ? 'success.main' : 'error.main'}
                fontWeight="bold"
              >
                {totalMovement > 0 ? '+' : ''}{formatCurrency(totalMovement)}
              </Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            
            {isForecast ? (
              // For forecast months, show budget and action summaries
              <>
                {/* Budget Movements */}
                {budgetMovements.length > 0 && (
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                      Budget Movements
                      <Tooltip title="Income and expense budgets affecting this account">
                        <Info fontSize="small" sx={{ ml: 0.5, color: theme.palette.text.secondary }} />
                      </Tooltip>
                    </Typography>
                    <Typography 
                      variant="body1" 
                      color={totalBudgetMovement >= 0 ? 'success.main' : 'error.main'}
                      sx={{ fontWeight: 'medium' }}
                    >
                      {totalBudgetMovement > 0 ? '+' : ''}{formatCurrency(totalBudgetMovement)}
                    </Typography>
                  </Box>
                )}

                {/* Action Movements */}
                {actionMovements.length > 0 && (
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                      Planned Actions
                      <Tooltip title="Scheduled income, expenses, and transfers affecting this account">
                        <Info fontSize="small" sx={{ ml: 0.5, color: theme.palette.text.secondary }} />
                      </Tooltip>
                    </Typography>
                    <Typography 
                      variant="body1" 
                      color={totalActionMovement >= 0 ? 'success.main' : 'error.main'}
                      sx={{ fontWeight: 'medium' }}
                    >
                      {totalActionMovement > 0 ? '+' : ''}{formatCurrency(totalActionMovement)}
                    </Typography>
                  </Box>
                )}
              </>
            ) : (
              // For actual months, show Money In/Out summary
              <>
                {/* Money In */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                    <TrendingUp color="success" fontSize="small" sx={{ mr: 0.5 }} />
                    Money In
                  </Typography>
                  <Typography 
                    variant="body1" 
                    color="success.main"
                    sx={{ fontWeight: 'medium' }}
                  >
                    +{formatCurrency(moneyIn)}
                  </Typography>
                </Box>
                
                {/* Money Out */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                    <TrendingDown color="error" fontSize="small" sx={{ mr: 0.5 }} />
                    Money Out
                  </Typography>
                  <Typography 
                    variant="body1" 
                    color="error.main"
                    sx={{ fontWeight: 'medium' }}
                  >
                    -{formatCurrency(moneyOut)}
                  </Typography>
                </Box>
                
                {/* Adjustment (if any) */}
                {Math.abs(adjustment) > 0.01 && (
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                      Adjustment
                      <Tooltip title="Balance adjustment due to interest, fees, or other changes not captured in transactions">
                        <Info fontSize="small" sx={{ ml: 0.5, color: theme.palette.text.secondary }} />
                      </Tooltip>
                    </Typography>
                    <Typography 
                      variant="body1" 
                      color={adjustment >= 0 ? 'success.main' : 'error.main'}
                      sx={{ fontWeight: 'medium' }}
                    >
                      {adjustment > 0 ? '+' : ''}{formatCurrency(adjustment)}
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Paper>

          {/* Ending Balance */}
          <Paper 
            variant="outlined" 
            sx={{ 
              p: 2, 
              mb: 2, 
              borderRadius: 3,
              boxShadow: 1,
              backgroundColor: theme.palette.grey[50],
              borderWidth: 2,
              borderColor: theme.palette.grey[300]
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1" fontWeight="bold">Ending Balance</Typography>
              <Typography 
                variant="h5" 
                fontWeight="bold"
                color={endingBalance >= 0 ? 'success.main' : 'error.main'}
              >
                {formatCurrency(endingBalance)}
              </Typography>
            </Box>
          </Paper>
        </Box>

        <Divider sx={{ mb: 2 }} />
        {/* Detailed Movement Breakdown - Different for actual vs forecast */}
        <Typography variant="h6" gutterBottom fontWeight="bold">Detailed Movements</Typography>

        {isForecast ? (
          // For forecast months - Show budget and action tables
          <>
            {/* Budget Movements Table */}
            {budgetMovements.length > 0 && (
              <>
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                  Budget Movements
                  <Chip 
                    label={`${budgetMovements.length} items`} 
                    size="small" 
                    sx={{ ml: 1 }} 
                    variant="outlined"
                  />
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Type</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {budgetMovements.map((movement, index) => (
                        <TableRow key={`budget-${index}`} hover>
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              {movement.type === 'income' ? (
                                <TrendingUp color="success" fontSize="small" sx={{ mr: 1 }} />
                              ) : (
                                <TrendingDown color="error" fontSize="small" sx={{ mr: 1 }} />
                              )}
                              {movement.type.charAt(0).toUpperCase() + movement.type.slice(1)}
                            </Box>
                          </TableCell>
                          <TableCell>{movement.category}</TableCell>
                          <TableCell>{movement.description}</TableCell>
                          <TableCell 
                            align="right"
                            sx={{ 
                              color: movement.amount >= 0 ? 'success.main' : 'error.main',
                              fontWeight: 'medium'
                            }}
                          >
                            {movement.amount > 0 ? '+' : ''}{formatCurrency(movement.amount)}
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Total Row */}
                      <TableRow 
                        sx={{ 
                          backgroundColor: theme.palette.grey[100],
                          '& .MuiTableCell-root': {
                            fontWeight: 'bold',
                          }
                        }}
                      >
                        <TableCell colSpan={3}>Total Budget Movements</TableCell>
                        <TableCell 
                          align="right"
                          sx={{ 
                            color: totalBudgetMovement >= 0 ? 'success.main' : 'error.main',
                          }}
                        >
                          {totalBudgetMovement > 0 ? '+' : ''}{formatCurrency(totalBudgetMovement)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {/* Action Movements Table */}
            {actionMovements.length > 0 && (
              <>
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                  Planned Actions
                  <Chip 
                    label={`${actionMovements.length} items`} 
                    size="small" 
                    sx={{ ml: 1 }} 
                    variant="outlined"
                  />
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Action</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>From/To</TableCell>
                        <TableCell align="right">Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {actionMovements.map((action, index) => (
                        <TableRow key={`action-${index}`} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <Box display="flex" alignItems="center">
                              {getFrequencyIcon(action.frequency)}
                              <Box ml={action.frequency && action.frequency !== 'once' ? 1 : 0}>
                                {action.date}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>{action.name}</TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              {action.type === 'income' ? (
                                <TrendingUp color="success" fontSize="small" sx={{ mr: 1 }} />
                              ) : action.type === 'expense' ? (
                                <TrendingDown color="error" fontSize="small" sx={{ mr: 1 }} />
                              ) : (
                                <SwapHoriz color="primary" fontSize="small" sx={{ mr: 1 }} />
                              )}
                              {action.type.charAt(0).toUpperCase() + action.type.slice(1)}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {action.type === 'transfer' 
                              ? `${action.sourceAccount} → ${action.destAccount}` 
                              : action.type === 'income'
                                ? `From: ${action.sourceAccount || 'External'}`
                                : `To: ${action.destAccount || 'External'}`}
                          </TableCell>
                          <TableCell 
                            align="right"
                            sx={{ 
                              color: action.amount >= 0 ? 'success.main' : 'error.main',
                              fontWeight: 'medium'
                            }}
                          >
                            {action.amount > 0 ? '+' : ''}{formatCurrency(action.amount)}
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Total Row */}
                      <TableRow 
                        sx={{ 
                          backgroundColor: theme.palette.grey[100],
                          '& .MuiTableCell-root': {
                            fontWeight: 'bold',
                          }
                        }}
                      >
                        <TableCell colSpan={4}>Total Action Movements</TableCell>
                        <TableCell 
                          align="right"
                          sx={{ 
                            color: totalActionMovement >= 0 ? 'success.main' : 'error.main',
                          }}
                        >
                          {totalActionMovement > 0 ? '+' : ''}{formatCurrency(totalActionMovement)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </>
        ) : (
          // For actual months - Show transactions table
          <>
            {monthlyTransactions.length > 0 ? (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 350, overflowY: 'auto', borderRadius: 3, boxShadow: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="right">Balance</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {monthlyTransactions.map((transaction) => (
                      <TableRow key={transaction.id} hover>
                        <TableCell>{formatDate(transaction.date)}</TableCell>
                        <TableCell>{transaction.description || transaction.payee || 'Unknown'}</TableCell>
                        <TableCell>{transaction.category || 'Uncategorized'}</TableCell>
                        <TableCell 
                          align="right"
                          sx={{ 
                            color: transaction.amount >= 0 ? 'success.main' : 'error.main',
                            fontWeight: 'medium'
                          }}
                        >
                          {transaction.amount > 0 ? '+' : ''}{formatCurrency(transaction.amount)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(transaction.balance || 0)}
                        </TableCell>
                      </TableRow>
                    ))}
                    
                    {/* Summary Rows */}
                    <TableRow 
                      sx={{ 
                        backgroundColor: theme.palette.grey[100],
                        '& .MuiTableCell-root': {
                          fontWeight: 'bold',
                        }
                      }}
                    >
                      <TableCell colSpan={3}>Total Money In</TableCell>
                      <TableCell 
                        align="right"
                        sx={{ color: 'success.main' }}
                      >
                        +{formatCurrency(moneyIn)}
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                    
                    <TableRow 
                      sx={{ 
                        backgroundColor: theme.palette.grey[100],
                        '& .MuiTableCell-root': {
                          fontWeight: 'bold',
                        }
                      }}
                    >
                      <TableCell colSpan={3}>Total Money Out</TableCell>
                      <TableCell 
                        align="right"
                        sx={{ color: 'error.main' }}
                      >
                        -{formatCurrency(moneyOut)}
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                    
                    {Math.abs(adjustment) > 0.01 && (
                      <TableRow 
                        sx={{ 
                          backgroundColor: theme.palette.grey[50],
                          '& .MuiTableCell-root': {
                            fontWeight: 'bold',
                          }
                        }}
                      >
                        <TableCell colSpan={3}>Adjustment</TableCell>
                        <TableCell 
                          align="right"
                          sx={{ 
                            color: adjustment >= 0 ? 'success.main' : 'error.main',
                          }}
                        >
                          {adjustment > 0 ? '+' : ''}{formatCurrency(adjustment)}
                        </TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    )}
                    
                    <TableRow 
                      sx={{ 
                        backgroundColor: theme.palette.primary.light,
                        '& .MuiTableCell-root': {
                          fontWeight: 'bold',
                          color: 'white'
                        }
                      }}
                    >
                      <TableCell colSpan={3}>Net Movement</TableCell>
                      <TableCell 
                        align="right"
                      >
                        {totalMovement > 0 ? '+' : ''}{formatCurrency(totalMovement)}
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderRadius: 3, boxShadow: 1 }}>
                <Typography color="text.secondary">
                  No transactions found for this account in {month}
                </Typography>
              </Paper>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AccountBalanceDetailDialog;